import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  const business = JSON.parse(localStorage.getItem('business') || '{}');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (business.id) config.headers['x-business-id'] = business.id;
  // Scope working requests (clients, route planner) to the active location.
  // The backend ignores this for managers/crew (forced to their location) and
  // honors it for owners. Reports overrides it with an explicit query param.
  const activeLocation = localStorage.getItem('activeLocationId');
  if (activeLocation) config.headers['x-location-id'] = activeLocation;
  return config;
});

export const getClients = () => API.get('/clients');
export const getArchivedClients = () => API.get('/clients/archived');
export const getClientsDueToday = () => API.get('/clients/due-today');
export const addClient = (client) => API.post('/clients', client);
export const updateClient = (id, client) => API.patch(`/clients/${id}`, client);
export const archiveClient = (id) => API.patch(`/clients/${id}`, { archived: true });
export const unarchiveClient = (id) => API.patch(`/clients/${id}`, { archived: false });
export const deleteClient = (id) => API.delete(`/clients/${id}`);
export const getRouteByDate = (date, timeSlot) => API.get(`/routes/date/${date}`, { params: { time_slot: timeSlot } });
export const getRouteVersions = () => API.get('/routes/versions');
export const getRouteVersionById = (id) => API.get(`/routes/versions/${id}`);
export const getRouteById = (id) => API.get(`/routes/${id}`);
// Lightweight per-route summaries across a date range (for the planning calendar).
export const getRouteCalendar = (from, to) => API.get('/routes/calendar', { params: { from, to } });
// Every shift's full route for one date (calendar day-detail panel).
export const getRouteDay = (date) => API.get(`/routes/day/${date}`);
// Every shift's routes WITH stops across a date range (weekly Kanban view).
export const getRouteWeek = (from, to) => API.get('/routes/week', { params: { from, to } });
// Reorder a route's stops without destroying them (preserves payments/approval).
export const reorderRoute = (id, stop_ids) => API.patch(`/routes/${id}/reorder`, { stop_ids });
// Calendar autosave — granular, non-destructive stop ops.
export const addStopToShift = (date, client_id, time_slot) => API.post(`/routes/day/${date}/stops`, { client_id, time_slot });
// Move a stop to another shift and/or another day (date optional).
export const moveStopToShift = (stopId, time_slot, date) =>
  API.patch(`/routes/stops/${stopId}/move`, { time_slot, ...(date ? { date } : {}) });
export const autoBuildDay = (date) => API.post(`/routes/day/${date}/auto-build`);
export const saveRoute = (route_date, stops, time_slot, crew_manager_id) =>
  API.post('/routes', { route_date, stops, time_slot, ...(crew_manager_id !== undefined ? { crew_manager_id } : {}) });
export const completeRoute = (id) => API.patch(`/routes/${id}`, { completed: true, completed_at: new Date().toISOString() });
export const deleteRoute = (id) => API.patch(`/routes/${id}`, { deleted: true });
// Clients whose next service date is due on/before a date and not yet routed.
export const getDueClients = (date) => API.get(`/routes/due/${date}`);
// Approve a route (locks stop order, unlocks the ordered map / navigation).
export const approveRoute = (id) => API.patch(`/routes/${id}`, { approved: true });
export const unapproveRoute = (id) => API.patch(`/routes/${id}`, { approved: false });
export const markStopComplete = (stopId, completed) => API.patch(`/routes/stops/${stopId}`, { completed });
export const markStopPaid = (stopId, paid) => API.patch(`/routes/stops/${stopId}`, { paid });
export const updateStopPayment = (stopId, payment) => API.patch(`/routes/stops/${stopId}`, payment);
export const markVisitPaid = (stopId) => API.patch(`/routes/stops/${stopId}`, { paid: true });
export const moveStopToSlot = (stopId, time_slot) => API.patch(`/routes/stops/${stopId}`, { time_slot });
export const deleteStopPayment = (stopId) => API.delete(`/routes/stops/${stopId}/payment`);
export const deleteClientVisit = (stopId) => API.delete(`/routes/stops/${stopId}`);
export const getClientHistory = (clientId) => API.get(`/clients/${clientId}/history`);
export const addManualVisit = (clientId, visit) => API.post(`/clients/${clientId}/visits`, visit);
// locationId: a specific location id, or 'all' for every location (owner only).
export const getCompletedRoutes = (from, to, locationId) =>
  API.get(`/routes/completed`, { params: { from, to, ...(locationId ? { location_id: locationId } : {}) } });
export const getAllRoutes = (from, to, locationId) =>
  API.get(`/routes/completed`, { params: { from, to, ...(locationId ? { location_id: locationId } : {}) } });

// Auth — fetch the caller's latest business + user record.
export const getMe = () => API.get('/auth/me');

// Account settings — update your own email / password (current password required).
export const updateEmail = (current_password, new_email) =>
  API.post('/auth/update-email', { current_password, new_email });
export const updatePassword = (current_password, new_password) =>
  API.post('/auth/update-password', { current_password, new_password });
export const updateProfile = (display_name) =>
  API.post('/auth/update-profile', { display_name });

// Billing — Stripe Checkout / Customer Portal.
export const startCheckout = (plan) => API.post('/billing/create-checkout-session', { plan });
export const openBillingPortal = () => API.post('/billing/create-portal-session');

// True if an error from one of the data endpoints indicates the user needs
// to subscribe before the backend will serve them.
export const isSubscriptionRequiredError = (err) =>
  err && err.response && err.response.status === 402;

// Referrals — public code check (no auth needed).
export const checkReferralCode = (code) => API.get(`/referrals/check/${encodeURIComponent(code)}`);

// Admin (Liz). 403 if you're not on the ADMIN_EMAILS list.
export const listTenants = () => API.get('/admin/tenants');

// Locations — owner manages; everyone can list.
export const listLocations = () => API.get('/locations');
export const createLocation = (payload) => API.post('/locations', payload);
export const updateLocation = (id, payload) => API.patch(`/locations/${id}`, payload);

// Team — owner/manager manage members.
export const listMembers = () => API.get('/team/members');
export const inviteMember = (payload) => API.post('/team/invite', payload);
export const updateMember = (id, payload) => API.patch(`/team/members/${id}`, payload);
export const removeMember = (id) => API.delete(`/team/members/${id}`);

// Referrals — admin (Liz). 403 if you're not on the ADMIN_EMAILS list.
export const listPartners = () => API.get('/referrals/partners');
export const createPartner = (payload) => API.post('/referrals/partners', payload);
export const updatePartner = (id, payload) => API.patch(`/referrals/partners/${id}`, payload);
export const resetPartnerLogin = (id) => API.post(`/referrals/partners/${id}/reset-login`);
export const listReferrals = () => API.get('/referrals');
export const getPayoutReport = () => API.get('/referrals/payout-report');
export const markReferralPaid = (id, amount_cents) => API.post(`/referrals/${id}/mark-paid`, { amount_cents });
export const unmarkReferralPaid = (id) => API.post(`/referrals/${id}/unmark-paid`);